import React, { useState, useEffect, useCallback } from 'react';

import debounce from 'lodash.debounce';
import { StyledTableContent, StyledTableContentHeader, StyledTableRow } from 'shared/ui/Table/ui/StyledTableContent';

import {
  Typography,
  IconButton,
  Box,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Menu,
  MenuItem,
  Checkbox,
  Popover,
  List,
  Button,
} from '@mui/material';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useReportDownload } from 'widgets/competitions-b2b/reports/lib';

interface IProps<FilterType> {
  title: string;
  fields: Array<{
    field: string;
    title: string;
    width: string;
  }>;
  list: any;
  onSearch?: (search?: string) => void;
  search?: string;
  setSearch?: (search: string) => void;
  onShowSearch?: (show: boolean) => void;
  onClearSearch?: () => void;
  onClearFilter?: () => void;
  isShowSearch?: boolean;
  onDownload?: () => void;
  applyFilter?: (field: keyof FilterType, value: any) => void;
  filterFields?: Array<{
    title: string;
    field: string;
    list:
      | Array<{
          title: string;
          value: any;
        }>
      | any;
  }>;
  filter?: Record<keyof FilterType, any>;
  count: number;
  page: number;
  perPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onRowClick: (param: any) => void;
  onDeleteClick?: (param: any) => void;
  onChangeClick?: (param: any) => void;
  searchPlaceholder?: string;
}

export const TableContentForCompetitionsB2B = <FilterType,>({
  title,
  onSearch,
  onDownload,
  onClearFilter,
  fields,
  list,
  onPageChange,
  onRowsPerPageChange,
  count,
  page,
  perPage,
  onRowClick,
  onDeleteClick,
  onChangeClick,
  applyFilter,
  isShowSearch,
  onShowSearch,
  onClearSearch,
  search,
  setSearch,
  filterFields,
  filter,
  searchPlaceholder,
}: IProps<FilterType>) => {
  const [sortFields, setSortFields] = useState<Array<string>>([]);
  const [filterOptionsList, setFilterOptionsList] = useState<{
    field: string;
    list: Array<{
      title: string;
      value: any;
    }>;
  }>({
    field: '',
    list: [],
  });

  const [fieldsShowAnchorEl, setFieldsShowAnchorEl] = useState<null | HTMLElement>(null);
  const [filterShowAnchorEl, setFilterShowAnchorEl] = useState<null | HTMLElement>(null);
  const [filterOptionsAnchorEl, setFilterOptionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isFieldsShow = Boolean(fieldsShowAnchorEl);
  const isFilterShow = Boolean(filterShowAnchorEl);
  const isFilterOptionsShow = Boolean(filterOptionsAnchorEl);

  const handleOpenFieldsShowMenu = (event: React.MouseEvent<HTMLElement>) => {
    setFieldsShowAnchorEl(event.currentTarget);
  };

  const handleOpenFilterShowMenu = (event: React.MouseEvent<HTMLElement>) => {
    setFilterShowAnchorEl(event.currentTarget);
  };

  const handleCloseFieldsShowMenu = () => {
    setFieldsShowAnchorEl(null);
  };

  const handleCloseFilterShowMenu = () => {
    setFilterShowAnchorEl(null);
  };

  const handleFilterOptionsOpen = (event: React.MouseEvent<any>) => {
    setFilterOptionsAnchorEl(event.currentTarget);
  };

  const handleFilterOptionsClose = () => {
    setFilterOptionsAnchorEl(null);
    setFilterOptionsList({
      field: '',
      list: [],
    });
  };

  const debounceSearch = useCallback(
    debounce(() => {
      onSearch!();
    }, 300),
    [],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSearch) {
      setSearch!(e.target.value);
      debounceSearch();
    }
  };

  const handleSortFields = (field: string) => {
    if (sortFields.includes(field)) {
      const sortedFields = [...sortFields].filter((inField) => inField !== field);
      setSortFields(sortedFields);
    } else {
      setSortFields((prev) => [...prev, field]);
    }
  };

  const handleClearFilter = () => {
    setFilterShowAnchorEl(null);
    if (onClearFilter) {
      onClearFilter();
    }
  };

  useEffect(() => {
    const forSortFields: Array<string> = [];
    fields.forEach((field) => {
      forSortFields.push(field.title);
    });

    setSortFields(forSortFields);
  }, []);

  const download = useReportDownload(undefined, title);

  return (
    <StyledTableContent>
      <StyledTableContentHeader>
        {isShowSearch ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SearchOutlinedIcon />
            <TextField
              autoFocus
              focused={isShowSearch}
              value={search}
              variant="standard"
              size="small"
              sx={{ width: '425px', pl: '5px', pr: '10px', marginTop: '-5px' }}
              onChange={handleSearch}
              placeholder={searchPlaceholder}
            />
            <IconButton onClick={() => onClearSearch!()}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
        ) : (
          <div
            className="title"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography mr={4}>{title}</Typography>
            <Button
              variant="contained"
              onClick={download}
              sx={{ textTransform: 'none' }}
            >
              Скачать отчет по всем соревнованиям
            </Button>
          </div>
        )}
        <div className="actions">
          {onSearch ? (
            <IconButton
              onClick={() => {
                onShowSearch!(!isShowSearch);
              }}
            >
              <SearchOutlinedIcon />
            </IconButton>
          ) : (
            false
          )}
          {onDownload ? (
            <IconButton onClick={onDownload}>
              <FileDownloadOutlinedIcon />
            </IconButton>
          ) : (
            false
          )}
          <IconButton onClick={handleOpenFieldsShowMenu}>
            <ViewWeekOutlinedIcon />
          </IconButton>
          <Menu
            anchorEl={fieldsShowAnchorEl}
            open={isFieldsShow}
            onClose={handleCloseFieldsShowMenu}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                minWidth: '220px',
              }}
            >
              <Typography sx={{ fontSize: '14px' }}>Показывать поля</Typography>
              <IconButton
                sx={{ color: '#000' }}
                onClick={handleCloseFieldsShowMenu}
              >
                <CloseOutlinedIcon />
              </IconButton>
            </Box>
            {fields.map(({ title }, index) => (
              <MenuItem
                key={index}
                onClick={() => handleSortFields(title)}
              >
                <Checkbox
                  size="small"
                  disableRipple
                  checked={sortFields.includes(title)}
                />
                <Typography sx={{ fontSize: '14px' }}>{title}</Typography>
              </MenuItem>
            ))}
          </Menu>
          {applyFilter ? (
            <>
              <IconButton onClick={handleOpenFilterShowMenu}>
                <FilterAltOutlinedIcon />
              </IconButton>
              <Menu
                anchorEl={filterShowAnchorEl}
                open={isFilterShow}
                onClose={handleCloseFilterShowMenu}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    minWidth: '220px',
                  }}
                >
                  <Typography sx={{ fontSize: '14px' }}>Выбрать данные</Typography>
                  <IconButton
                    sx={{ color: '#000' }}
                    onClick={handleCloseFilterShowMenu}
                  >
                    <CloseOutlinedIcon />
                  </IconButton>
                </Box>
                {filterFields?.map(({ field, list, title }, index) => (
                  <MenuItem
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onClick={(e) => {
                      handleFilterOptionsOpen(e);
                      setFilterOptionsList({
                        field,
                        list,
                      });
                    }}
                  >
                    <Typography sx={{ fontSize: '14px' }}>{title}</Typography>
                    <ArrowRightIcon />
                  </MenuItem>
                ))}
                <MenuItem onClick={handleClearFilter}>
                  <FilterAltOffOutlinedIcon />
                  <Typography sx={{ fontWeight: '500' }}>Сбросить фильтр</Typography>
                </MenuItem>
              </Menu>
              <Popover
                anchorEl={filterOptionsAnchorEl}
                open={isFilterOptionsShow}
                onClose={handleFilterOptionsClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <List>
                  {filterOptionsList.list &&
                    filterOptionsList.list.map((filterOption, index) => (
                      <MenuItem
                        key={index}
                        onClick={() => {
                          applyFilter(filterOptionsList.field as keyof FilterType, filterOption.value);
                        }}
                      >
                        <Checkbox
                          disableRipple
                          checked={
                            filter && filter[filterOptionsList.field as keyof FilterType].includes(filterOption.value)
                          }
                        />
                        <Typography sx={{ fontSize: '14px' }}>{filterOption.title}</Typography>
                      </MenuItem>
                    ))}
                </List>
              </Popover>
            </>
          ) : (
            false
          )}
        </div>
      </StyledTableContentHeader>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {fields.map((field, index) =>
                sortFields.includes(field.title) ? (
                  <TableCell
                    key={index}
                    style={{ width: field.width }}
                  >
                    {field.title}
                  </TableCell>
                ) : (
                  false
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {list &&
              list.list &&
              list.list.map((user: any, index: any) => (
                <StyledTableRow
                  key={index}
                  hover
                  onClick={() => onRowClick(user.id)}
                >
                  {fields.map((field, index) =>
                    sortFields.includes(field.title) ? <TableCell key={index}>{user[field.field]}</TableCell> : false,
                  )}
                  {(title === 'Список SKU' ||
                    title === 'Шаги механики' ||
                    title === 'Номера и пользователи торговой точки' ||
                    title === 'Торговые точки, участвующие в акции' ||
                    title === 'Список категории' ||
                    title === 'Список партнера' ||
                    title === 'Товары, доступные для продажи' ||
                    title === 'Список баннеров' ||
                    title === 'Список торговых точек' ||
                    title === 'Промо-акции для пользователей B2B') &&
                  onDeleteClick ? (
                    <TableCell align="center">
                      <IconButton onClick={() => onDeleteClick(user.id)}>
                        <CloseOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  ) : (
                    ''
                  )}
                  {(title === 'Шаги механики' || title === 'Список категории' || title === 'Список партнера') &&
                  onChangeClick ? (
                    <TableCell align="center">
                      <IconButton onClick={() => (onChangeClick ? onChangeClick(user.id) : undefined)}>
                        <EditOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  ) : (
                    ''
                  )}
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 20, 30]}
        count={count}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        rowsPerPage={perPage}
        labelRowsPerPage="Строк на странице"
        showFirstButton={false}
      />
    </StyledTableContent>
  );
};
